import React, { useCallback, useMemo } from 'react';
import { useEditorStore } from '~/shell/Store';
import { useTranslationScript } from './useTranslationScript';
import { TranslationField } from './TranslationField';
import {
  TranslationFilters,
  type StatusCounts,
  type MediaCounts
} from './TranslationFilters';
import type {
  LocalizedField,
  MediaEntry,
  SequenceScript,
  SubitemScript,
  TranslationUpdatePatch,
  AiLoadingKey
} from './types';
import {
  flattenSubitems,
  subitemFieldLabel
} from './ScriptTranslationView.utils';
import {
  Card,
  Container,
  HeaderArea,
  HeaderColumn,
  HeaderControls,
  HeaderLabel,
  LockIcon,
  ScrollWrapper,
  Section,
  SectionDivider,
  Select,
  TranslationLayout,
  TwoColumnHeader,
  SkeletonLine,
  SkeletonPill,
  FieldCell,
  SectionRow
} from './ScriptTranslationView.styles';

export const ScriptTranslationView: React.FC = () => {
  const training = useEditorStore(state => state.training);
  const sharedSequenceIds = useMemo(
    () =>
      new Set(
        training?.chapters
          ?.flatMap(chapter => chapter.chapterSequences || [])
          .filter(
            chapterSequence => chapterSequence.sequence?.editable === false
          )
          .map(chapterSequence => chapterSequence.sequence.id) || []
      ),
    [training]
  );
  const {
    loading,
    scriptOriginal,
    scriptDraft,
    targetLocale,
    aiLoading,
    aiLoadingKey,
    availableLocales,
    effectiveTargetLocale,
    buildTranslationsPatch,
    statusText,
    saveError,
    filtersOpen,
    activeFilters,
    mediaFiltersActive,
    doSave,
    updateTarget,
    aiTranslate,
    changeTargetLocale,
    setFiltersOpen,
    toggleFilter,
    shouldRenderField,
    shouldShowTargetField
  } = useTranslationScript();

  const { statusCounts, mediaCounts } = useMemo((): {
    statusCounts: StatusCounts;
    mediaCounts: MediaCounts;
  } => {
    const statusCounts: StatusCounts = {
      missing: 0,
      stale: 0,
      manual: 0,
      auto: 0
    };
    const mediaCounts: MediaCounts = { audio: 0, video: 0, image: 0 };

    if (!scriptDraft) return { statusCounts, mediaCounts };

    const bumpField = (field?: LocalizedField | null) => {
      if (!field || field.source == null) return;
      if (field.missing) statusCounts.missing += 1;
      if (field.stale) statusCounts.stale += 1;
      const sourceKind = field.source_kind;
      if (sourceKind === 'manual') statusCounts.manual += 1;
      if (sourceKind === 'auto') statusCounts.auto += 1;
    };

    type MediaFlags = {
      hasAudio: boolean;
      hasVideo: boolean;
      hasImage: boolean;
    };

    const processMedia = (
      mediaItems: MediaEntry[] | undefined,
      flags: MediaFlags
    ): void => {
      for (const mediaItem of mediaItems ?? []) {
        bumpField(mediaItem.script);
        bumpField(mediaItem.name);

        const scriptSource = (mediaItem.script?.source ?? '').trim();
        const nameSource = (mediaItem.name?.source ?? '').trim();

        if (mediaItem.type === 'Audio' && scriptSource) flags.hasAudio = true;
        if (mediaItem.type === 'Video' && scriptSource) flags.hasVideo = true;
        if (mediaItem.type === 'Picture' && nameSource) flags.hasImage = true;
      }
    };

    bumpField(scriptDraft.training?.title);
    bumpField(scriptDraft.training?.description);

    for (const chapter of scriptDraft.chapters) {
      bumpField(chapter.title);

      for (const sequence of chapter.sequences) {
        bumpField(sequence.title);

        const flags: MediaFlags = {
          hasAudio: false,
          hasVideo: false,
          hasImage: false
        };

        processMedia(sequence.media, flags);

        for (const subitem of flattenSubitems(sequence.subitems)) {
          bumpField(subitem.title);
          bumpField(subitem.subtitle);
          bumpField(subitem.body);
          processMedia(subitem.media, flags);
        }

        if (flags.hasAudio) mediaCounts.audio += 1;
        if (flags.hasVideo) mediaCounts.video += 1;
        if (flags.hasImage) mediaCounts.image += 1;
      }
    }

    return { statusCounts, mediaCounts };
  }, [scriptDraft]);

  const mediaHasVisibleField = useCallback(
    (media?: MediaEntry[]): boolean => {
      if (!Array.isArray(media)) return false;
      for (const mediaItem of media) {
        if (shouldRenderField(mediaItem.script, mediaItem.type)) return true;
        if (shouldRenderField(mediaItem.name, mediaItem.type)) return true;
      }
      return false;
    },
    [shouldRenderField]
  );

  const sequenceHasVisibleRows = useCallback(
    (sequence: SequenceScript): boolean => {
      const hasVisibleMediaHere = mediaHasVisibleField(sequence.media);
      let hasVisibleMediaInSubitems = false;

      for (const subitem of flattenSubitems(sequence.subitems)) {
        if (mediaHasVisibleField(subitem.media)) {
          hasVisibleMediaInSubitems = true;
          break;
        }
      }

      if (mediaFiltersActive) {
        return hasVisibleMediaHere || hasVisibleMediaInSubitems;
      }

      if (shouldRenderField(sequence.title)) return true;
      if (hasVisibleMediaHere) return true;

      for (const subitem of flattenSubitems(sequence.subitems)) {
        if (shouldRenderField(subitem.title)) return true;
        if (shouldRenderField(subitem.subtitle)) return true;
        if (shouldRenderField(subitem.body)) return true;
        if (mediaHasVisibleField(subitem.media)) return true;
      }

      return false;
    },
    [shouldRenderField, mediaFiltersActive, mediaHasVisibleField]
  );

  const hasVisibleRows = useMemo(() => {
    if (!scriptDraft) return false;

    if (mediaFiltersActive) {
      for (const chapter of scriptDraft.chapters) {
        for (const sequence of chapter.sequences) {
          if (sequenceHasVisibleRows(sequence)) return true;
        }
      }
      return false;
    }

    if (shouldRenderField(scriptDraft.training.title)) return true;
    if (shouldRenderField(scriptDraft.training.description)) return true;

    for (const chapter of scriptDraft.chapters) {
      if (shouldRenderField(chapter.title)) return true;
      for (const sequence of chapter.sequences) {
        if (sequenceHasVisibleRows(sequence)) return true;
      }
    }

    return false;
  }, [
    scriptDraft,
    shouldRenderField,
    mediaFiltersActive,
    sequenceHasVisibleRows
  ]);

  const requestAiForTraining = (field: 'title' | 'description') => {
    const translationKey =
      field === 'title' ? 'title_translations' : 'description_translations';
    const patch: TranslationUpdatePatch = {
      training: { [translationKey]: { [effectiveTargetLocale]: '' } }
    };
    const key: AiLoadingKey = { type: 'training', field };
    aiTranslate(patch, key);
  };

  const requestAiForChapter = (chapterId: number) => {
    const patch: TranslationUpdatePatch = {
      chapters: [
        { id: chapterId, title_translations: { [effectiveTargetLocale]: '' } }
      ]
    };
    const key: AiLoadingKey = { type: 'chapter', chapterId };
    aiTranslate(patch, key);
  };

  const requestAiForSequenceTitle = (sequenceId: number) => {
    const patch: TranslationUpdatePatch = {
      sequences: [
        { id: sequenceId, title_translations: { [effectiveTargetLocale]: '' } }
      ]
    };
    const key: AiLoadingKey = { type: 'sequence', sequenceId };
    aiTranslate(patch, key);
  };

  const requestAiForSequenceMedia = (
    sequenceId: number,
    mediaId: number,
    isName: boolean
  ) => {
    const mediaTranslations = isName
      ? { name_translations: { [effectiveTargetLocale]: '' } }
      : { script_translations: { [effectiveTargetLocale]: '' } };
    const patch: TranslationUpdatePatch = {
      sequences: [
        { id: sequenceId, media: [{ id: mediaId, ...mediaTranslations }] }
      ]
    };
    const key: AiLoadingKey = isName
      ? { type: 'sequence-media-name', sequenceId, mediaId }
      : { type: 'sequence-media', sequenceId, mediaId };
    aiTranslate(patch, key);
  };

  const requestAiForSubitemField = (
    sequenceId: number,
    subitemId: number,
    field: 'title' | 'subtitle' | 'body'
  ) => {
    const translationKey =
      field === 'title'
        ? 'title_translations'
        : field === 'subtitle'
          ? 'subtitle_translations'
          : 'body_translations';
    const patch: TranslationUpdatePatch = {
      sequences: [
        {
          id: sequenceId,
          subitems: [
            { id: subitemId, [translationKey]: { [effectiveTargetLocale]: '' } }
          ]
        }
      ]
    };
    const key: AiLoadingKey = { type: 'subitem', sequenceId, subitemId, field };
    aiTranslate(patch, key);
  };

  const requestAiForSubitemMedia = (
    sequenceId: number,
    subitemId: number,
    mediaId: number,
    isName: boolean
  ) => {
    const mediaTranslations = isName
      ? { name_translations: { [effectiveTargetLocale]: '' } }
      : { script_translations: { [effectiveTargetLocale]: '' } };
    const patch: TranslationUpdatePatch = {
      sequences: [
        {
          id: sequenceId,
          subitems: [
            { id: subitemId, media: [{ id: mediaId, ...mediaTranslations }] }
          ]
        }
      ]
    };
    const key: AiLoadingKey = isName
      ? { type: 'subitem-media-name', sequenceId, subitemId, mediaId }
      : { type: 'subitem-media', sequenceId, subitemId, mediaId };
    aiTranslate(patch, key);
  };

  const renderSubitemRows = (
    sequenceId: number,
    subitem: SubitemScript,
    subitemPath: readonly (string | number)[],
    depth: number
  ): React.ReactNode => {
    const keyPrefix = `seq-${sequenceId}-subitem-${subitem.id}-${depth}`;
    const kind = subitem.kind;
    const hasPendingChanges = !!buildTranslationsPatch;

    return (
      <React.Fragment key={keyPrefix}>
        {subitem.media?.map((mediaItem, mediaIndex) => {
          if (!shouldRenderField(mediaItem.script, mediaItem.type)) return null;
          return (
            <TranslationField
              key={`${keyPrefix}-media-${mediaItem.id}-script`}
              label={subitemFieldLabel(kind, `${mediaItem.type} Script`)}
              field={mediaItem.script}
              path={[...subitemPath, 'media', mediaIndex, 'script']}
              aiLoadingKey={aiLoadingKey}
              fieldAiKey={{
                type: 'subitem-media',
                sequenceId,
                subitemId: subitem.id,
                mediaId: mediaItem.id
              }}
              aiLoading={aiLoading}
              hasPendingChanges={hasPendingChanges}
              shouldShowField={shouldShowTargetField}
              onUpdate={updateTarget}
              onSave={() => doSave()}
              onGenerateAi={() =>
                requestAiForSubitemMedia(
                  sequenceId,
                  subitem.id,
                  mediaItem.id,
                  false
                )
              }
            />
          );
        })}

        {subitem.media?.map((mediaItem, mediaIndex) => {
          if (!shouldRenderField(mediaItem.name, mediaItem.type)) return null;
          const labelText =
            mediaItem.type === 'Picture'
              ? 'Image Alt Text'
              : `${mediaItem.type} Name`;
          return (
            <TranslationField
              key={`${keyPrefix}-media-${mediaItem.id}-name`}
              label={subitemFieldLabel(kind, labelText)}
              field={mediaItem.name}
              path={[...subitemPath, 'media', mediaIndex, 'name']}
              aiLoadingKey={aiLoadingKey}
              fieldAiKey={{
                type: 'subitem-media-name',
                sequenceId,
                subitemId: subitem.id,
                mediaId: mediaItem.id
              }}
              aiLoading={aiLoading}
              hasPendingChanges={hasPendingChanges}
              shouldShowField={shouldShowTargetField}
              onUpdate={updateTarget}
              onSave={() => doSave()}
              onGenerateAi={() =>
                requestAiForSubitemMedia(
                  sequenceId,
                  subitem.id,
                  mediaItem.id,
                  true
                )
              }
            />
          );
        })}

        {shouldRenderField(subitem.title) && (
          <TranslationField
            label={subitemFieldLabel(kind, 'Title')}
            field={subitem.title}
            path={[...subitemPath, 'title']}
            aiLoadingKey={aiLoadingKey}
            fieldAiKey={{
              type: 'subitem',
              sequenceId,
              subitemId: subitem.id,
              field: 'title'
            }}
            aiLoading={aiLoading}
            hasPendingChanges={hasPendingChanges}
            shouldShowField={shouldShowTargetField}
            onUpdate={updateTarget}
            onSave={() => doSave()}
            onGenerateAi={() =>
              requestAiForSubitemField(sequenceId, subitem.id, 'title')
            }
          />
        )}

        {shouldRenderField(subitem.subtitle) && (
          <TranslationField
            label={subitemFieldLabel(kind, 'Subtitle')}
            field={subitem.subtitle}
            path={[...subitemPath, 'subtitle']}
            aiLoadingKey={aiLoadingKey}
            fieldAiKey={{
              type: 'subitem',
              sequenceId,
              subitemId: subitem.id,
              field: 'subtitle'
            }}
            aiLoading={aiLoading}
            hasPendingChanges={hasPendingChanges}
            shouldShowField={shouldShowTargetField}
            onUpdate={updateTarget}
            onSave={() => doSave()}
            onGenerateAi={() =>
              requestAiForSubitemField(sequenceId, subitem.id, 'subtitle')
            }
          />
        )}

        {shouldRenderField(subitem.body) && (
          <TranslationField
            label={subitemFieldLabel(kind, 'Body')}
            field={subitem.body}
            path={[...subitemPath, 'body']}
            aiLoadingKey={aiLoadingKey}
            fieldAiKey={{
              type: 'subitem',
              sequenceId,
              subitemId: subitem.id,
              field: 'body'
            }}
            aiLoading={aiLoading}
            hasPendingChanges={hasPendingChanges}
            shouldShowField={shouldShowTargetField}
            onUpdate={updateTarget}
            onSave={() => doSave()}
            onGenerateAi={() =>
              requestAiForSubitemField(sequenceId, subitem.id, 'body')
            }
          />
        )}

        {subitem.subitems?.map((childSubitem, childIndex) =>
          renderSubitemRows(
            sequenceId,
            childSubitem,
            [...subitemPath, 'subitems', childIndex],
            depth + 1
          )
        )}
      </React.Fragment>
    );
  };

  const SkeletonRow = () => (
    <SectionRow>
      <FieldCell>
        <SkeletonLine />
      </FieldCell>
      <FieldCell>
        <SkeletonLine />
      </FieldCell>
    </SectionRow>
  );

  if (loading || !scriptDraft || !scriptOriginal) {
    return (
      <TranslationLayout>
        <HeaderArea>
          <TwoColumnHeader>
            <HeaderColumn>
              <HeaderLabel>
                English <LockIcon />
              </HeaderLabel>
            </HeaderColumn>
            <HeaderControls>
              <SkeletonPill style={{ width: 140 }} />
              <SkeletonPill style={{ width: 100 }} />
            </HeaderControls>
          </TwoColumnHeader>
        </HeaderArea>
        <ScrollWrapper>
          <Container>
            <Card>
              <SectionDivider>Training</SectionDivider>
              <Section>
                <SkeletonRow />
                <SkeletonRow />
              </Section>
            </Card>
            <Card>
              <SectionDivider>Chapter</SectionDivider>
              <Section>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </Section>
            </Card>
          </Container>
        </ScrollWrapper>
      </TranslationLayout>
    );
  }

  const hasPendingChanges = !!buildTranslationsPatch;

  return (
    <TranslationLayout>
      <HeaderArea>
        <TwoColumnHeader>
          <HeaderColumn>
            <HeaderLabel>
              English <LockIcon />
            </HeaderLabel>
          </HeaderColumn>
          <HeaderControls>
            <Select
              disabled={loading}
              value={targetLocale || scriptDraft.targetLocale}
              onChange={event => changeTargetLocale(event.target.value)}
              style={{ minWidth: '120px' }}
            >
              {availableLocales
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(localeOption => localeOption.locale !== 'en')
                .map(localeOption => (
                  <option
                    key={`tgt-${localeOption.locale}`}
                    value={localeOption.locale}
                  >
                    {localeOption.name}
                  </option>
                ))}
            </Select>
            <TranslationFilters
              filtersOpen={filtersOpen}
              setFiltersOpen={setFiltersOpen}
              activeFilters={activeFilters}
              toggleFilter={toggleFilter}
              statusCounts={statusCounts}
              mediaCounts={mediaCounts}
            />
            {saveError ? (
              <span
                style={{
                  fontSize: 12,
                  color: '#c00',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                Save failed
                <button
                  type="button"
                  onClick={() => doSave()}
                  style={{
                    fontSize: 12,
                    padding: '2px 8px',
                    cursor: 'pointer',
                    border: '1px solid #c00',
                    borderRadius: 4,
                    background: '#fff',
                    color: '#c00'
                  }}
                >
                  Retry
                </button>
              </span>
            ) : statusText ? (
              <span style={{ fontSize: 12, color: '#777' }}>{statusText}</span>
            ) : null}
          </HeaderControls>
        </TwoColumnHeader>
      </HeaderArea>
      <ScrollWrapper>
        <Container>
          {(shouldRenderField(scriptDraft.training.title) ||
            shouldRenderField(scriptDraft.training.description)) && (
            <Card>
              <SectionDivider>Training</SectionDivider>
              <Section>
                {shouldRenderField(scriptDraft.training.title) && (
                  <TranslationField
                    label="Training Title"
                    field={scriptDraft.training.title}
                    path={['training', 'title']}
                    aiLoadingKey={aiLoadingKey}
                    fieldAiKey={{ type: 'training', field: 'title' }}
                    aiLoading={aiLoading}
                    hasPendingChanges={hasPendingChanges}
                    shouldShowField={shouldShowTargetField}
                    onUpdate={updateTarget}
                    onSave={() => doSave()}
                    onGenerateAi={() => requestAiForTraining('title')}
                  />
                )}
                {shouldRenderField(scriptDraft.training.description) && (
                  <TranslationField
                    label="Training Description"
                    field={scriptDraft.training.description}
                    path={['training', 'description']}
                    aiLoadingKey={aiLoadingKey}
                    fieldAiKey={{ type: 'training', field: 'description' }}
                    aiLoading={aiLoading}
                    hasPendingChanges={hasPendingChanges}
                    shouldShowField={shouldShowTargetField}
                    onUpdate={updateTarget}
                    onSave={() => doSave()}
                    onGenerateAi={() => requestAiForTraining('description')}
                  />
                )}
              </Section>
            </Card>
          )}

          {scriptDraft.chapters
            .map((chapter, chapterIndex) => ({ chapter, chapterIndex }))
            .filter(
              ({ chapter }) =>
                chapter.sequences.some(sequence =>
                  sequenceHasVisibleRows(sequence)
                ) || shouldRenderField(chapter.title)
            )
            .map(({ chapter, chapterIndex }) => (
              <Card key={chapter.id}>
                <SectionDivider id={`translation-chapter-${chapter.id}`}>
                  Chapter {chapter.position}:{' '}
                  {chapter.title?.source || 'Untitled'}
                </SectionDivider>
                <Section>
                  {shouldRenderField(chapter.title) && (
                    <TranslationField
                      label="Chapter Title"
                      field={chapter.title}
                      path={['chapters', chapterIndex, 'title']}
                      aiLoadingKey={aiLoadingKey}
                      fieldAiKey={{ type: 'chapter', chapterId: chapter.id }}
                      aiLoading={aiLoading}
                      hasPendingChanges={hasPendingChanges}
                      shouldShowField={shouldShowTargetField}
                      onUpdate={updateTarget}
                      onSave={() => doSave()}
                      onGenerateAi={() => requestAiForChapter(chapter.id)}
                    />
                  )}
                </Section>

                {chapter.sequences
                  .map((sequence, sequenceIndex) => ({
                    sequence,
                    sequenceIndex
                  }))
                  .filter(({ sequence }) => sequenceHasVisibleRows(sequence))
                  .map(({ sequence, sequenceIndex }) => (
                    <div key={sequence.id}>
                      <SectionDivider
                        style={{
                          background: '#fff',
                          borderBottomColor: '#ddd'
                        }}
                      >
                        Slide {sequence.position}
                        {sequence.title?.source
                          ? `: ${sequence.title.source}`
                          : ''}
                        {sharedSequenceIds.has(sequence.id)
                          ? ' (read-only shared slide)'
                          : ''}
                      </SectionDivider>
                      <Section
                        as="fieldset"
                        disabled={sharedSequenceIds.has(sequence.id)}
                        style={{
                          border: 0,
                          padding: 0,
                          margin: 0,
                          minWidth: 0
                        }}
                      >
                        {shouldRenderField(sequence.title) && (
                          <TranslationField
                            label="Slide Title"
                            field={sequence.title}
                            path={[
                              'chapters',
                              chapterIndex,
                              'sequences',
                              sequenceIndex,
                              'title'
                            ]}
                            aiLoadingKey={aiLoadingKey}
                            fieldAiKey={{
                              type: 'sequence',
                              sequenceId: sequence.id
                            }}
                            aiLoading={aiLoading}
                            hasPendingChanges={hasPendingChanges}
                            shouldShowField={shouldShowTargetField}
                            onUpdate={updateTarget}
                            onSave={() => doSave()}
                            onGenerateAi={() =>
                              requestAiForSequenceTitle(sequence.id)
                            }
                          />
                        )}

                        {sequence.media?.map((mediaItem, mediaIndex) => {
                          if (
                            !shouldRenderField(mediaItem.script, mediaItem.type)
                          )
                            return null;
                          return (
                            <TranslationField
                              key={`seq-${sequence.id}-media-${mediaItem.id}-script`}
                              label={`Slide ${mediaItem.type} Script`}
                              field={mediaItem.script}
                              path={[
                                'chapters',
                                chapterIndex,
                                'sequences',
                                sequenceIndex,
                                'media',
                                mediaIndex,
                                'script'
                              ]}
                              aiLoadingKey={aiLoadingKey}
                              fieldAiKey={{
                                type: 'sequence-media',
                                sequenceId: sequence.id,
                                mediaId: mediaItem.id
                              }}
                              aiLoading={aiLoading}
                              hasPendingChanges={hasPendingChanges}
                              shouldShowField={shouldShowTargetField}
                              onUpdate={updateTarget}
                              onSave={() => doSave()}
                              onGenerateAi={() =>
                                requestAiForSequenceMedia(
                                  sequence.id,
                                  mediaItem.id,
                                  false
                                )
                              }
                            />
                          );
                        })}

                        {sequence.media?.map((mediaItem, mediaIndex) => {
                          if (
                            !shouldRenderField(mediaItem.name, mediaItem.type)
                          )
                            return null;
                          const labelText =
                            mediaItem.type === 'Picture'
                              ? 'Image Alt Text'
                              : `${mediaItem.type} Name`;
                          return (
                            <TranslationField
                              key={`seq-${sequence.id}-media-${mediaItem.id}-name`}
                              label={`Slide ${labelText}`}
                              field={mediaItem.name}
                              path={[
                                'chapters',
                                chapterIndex,
                                'sequences',
                                sequenceIndex,
                                'media',
                                mediaIndex,
                                'name'
                              ]}
                              aiLoadingKey={aiLoadingKey}
                              fieldAiKey={{
                                type: 'sequence-media-name',
                                sequenceId: sequence.id,
                                mediaId: mediaItem.id
                              }}
                              aiLoading={aiLoading}
                              hasPendingChanges={hasPendingChanges}
                              shouldShowField={shouldShowTargetField}
                              onUpdate={updateTarget}
                              onSave={() => doSave()}
                              onGenerateAi={() =>
                                requestAiForSequenceMedia(
                                  sequence.id,
                                  mediaItem.id,
                                  true
                                )
                              }
                            />
                          );
                        })}

                        {sequence.subitems?.map((subitem, subitemIndex) =>
                          renderSubitemRows(
                            sequence.id,
                            subitem,
                            [
                              'chapters',
                              chapterIndex,
                              'sequences',
                              sequenceIndex,
                              'subitems',
                              subitemIndex
                            ],
                            0
                          )
                        )}
                      </Section>
                    </div>
                  ))}
              </Card>
            ))}

          {!hasVisibleRows && (
            <Card>
              <Section>
                <div style={{ padding: '12px', color: '#666' }}>No results</div>
              </Section>
            </Card>
          )}
        </Container>
      </ScrollWrapper>
    </TranslationLayout>
  );
};
